import React from 'react';
import { FiGlobe, FiTrendingUp, FiCheckCircle, FiClock, FiUsers, FiZap, FiShield, FiMonitor, FiEdit3 } from 'react-icons/fi';

const ERPWebsite = () => {
  const websiteFeatures = [
    {
      icon: <FiGlobe className="w-6 h-6" />,
      title: 'Unified Brand Experience',
      description: 'Your website and your ERP share the same design, colors, and branding. Parents and students experience a seamless transition from public website to private portal. No disjointed experiences or confusing navigation.',
      stat: '70% of parents value consistent branding across platforms',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiMonitor className="w-6 h-6" />,
      title: 'No Separate CMS',
      description: 'Your website content—pages, admissions info, announcements, events—is managed within the same platform. No need for a separate Content Management System. No separate login. No separate training.',
      stat: '85% of institutions want integrated content management',
      color: 'bg-emerald-50'
    },
    {
      icon: <FiUsers className="w-6 h-6" />,
      title: 'Parent and Student Portals',
      description: 'Parents and students can log in to check attendance, grades, schedules, and announcements. The same login works for the website and the portal. No separate credentials. No confusion.',
      stat: '90% of parents prefer single login for all platforms',
      color: 'bg-amber-50'
    },
    {
      icon: <FiEdit3 className="w-6 h-6" />,
      title: 'Easy Content Updates',
      description: 'Non-technical staff can update website content. No developer needed. No HTML knowledge required. Just simple, intuitive editing tools.',
      stat: '80% of institutions struggle with website updates',
      color: 'bg-rose-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Secure and Compliant',
      description: 'Student data is protected by enterprise-grade security. Parent and student portals are behind secure login. Your public website meets all data protection standards.',
      stat: '95% of institutions cite security as a top priority',
      color: 'bg-purple-50'
    },
    {
      icon: <FiTrendingUp className="w-6 h-6" />,
      title: 'SEO and Analytics Ready',
      description: 'Your website is built for search engines. SEO-friendly URLs, meta tags, and sitemaps are automatically generated. Analytics is built in. You see how parents find you.',
      stat: '75% of institutions want better SEO for their website',
      color: 'bg-blue-50'
    }
  ];

  const benefits = [
    {
      title: 'One Platform, All Functions',
      description: 'Manage your public website, parent portal, student portal, and ERP from one platform.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Reduced Management Complexity',
      description: 'No more managing separate systems for website, portal, and administration. One platform to learn, manage, and maintain.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Single User Experience',
      description: 'Parents, students, and teachers experience a unified platform. No confusion, no disjointed interfaces.',
      icon: <FiUsers className="w-5 h-5" />
    },
    {
      title: 'Cost Effective',
      description: 'Eliminate separate costs for website hosting, CMS licenses, and portal development. One subscription, multiple functions.',
      icon: <FiCheckCircle className="w-5 h-5" />
    },
    {
      title: 'Real-Time Data Display',
      description: 'Your website can display real-time data from the ERP—admission updates, event calendars, and announcement.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Scalable and Future-Ready',
      description: 'Add new features as you grow—online admissions, fee payment, alumni portal—without adding new systems.',
      icon: <FiTrendingUp className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Most educational institutions have two separate digital systems: a public website that showcases the institution, and an ERP that handles internal administration. These systems are often completely disconnected—different platforms, different logins, different designs.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        <strong className="text-slate-900">But what if one platform could do both?</strong> What if your website and your ERP were the same system? What if your public website could display real-time admission updates, event calendars, and announcements—all powered by your ERP?
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Here's how an educational ERP can also be a powerful public website—and why it matters for Indian institutions.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">70%</div>
          <div className="text-sm text-slate-500">Parents value consistent branding</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-emerald-600">90%</div>
          <div className="text-sm text-slate-500">Parents prefer single login</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">80%</div>
          <div className="text-sm text-slate-500">Struggle with website updates</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">How an ERP Powers Your Public Website</h2>

      {/* Website Features */}
      {websiteFeatures.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">📊 {item.stat}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Benefits Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Why Integrated Website + ERP Matters</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {benefits.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Separate Systems vs. Integrated ERP + Website</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Separate Systems</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Different platforms, different logins</span></li>
              <li className="flex items-start gap-2">• <span>Inconsistent branding</span></li>
              <li className="flex items-start gap-2">• <span>Manual content updates</span></li>
              <li className="flex items-start gap-2">• <span>Higher costs and complexity</span></li>
              <li className="flex items-start gap-2">• <span>Disjointed user experience</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Integrated with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>One platform, one login</span></li>
              <li className="flex items-start gap-2">• <span>Consistent branding</span></li>
              <li className="flex items-start gap-2">• <span>Real-time data updates</span></li>
              <li className="flex items-start gap-2">• <span>Lower costs and simpler management</span></li>
              <li className="flex items-start gap-2">• <span>Seamless user experience</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* The Parent Perspective */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Parent and Student Experience</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          When parents visit your website and then log in to the parent portal, they should feel like they're in the same place. Same design, same navigation, same experience. This builds trust and confidence in your institution.
        </p>
        <ul className="text-amber-800 leading-relaxed space-y-2">
          <li className="flex items-start gap-2">• <span>Parents see admission announcements on the website</span></li>
          <li className="flex items-start gap-2">• <span>They click a button and log in to check their child's progress</span></li>
          <li className="flex items-start gap-2">• <span>The experience is seamless. No confusion. No frustration.</span></li>
          <li className="flex items-start gap-2">• <span>This builds loyalty and trust in your institution.</span></li>
        </ul>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon is the Best Choice for Integrated Website + ERP</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🌐</div>
            <h4 className="font-bold text-slate-900 text-sm">Built-in Website Builder</h4>
            <p className="text-xs text-slate-600">Create and manage your website without coding</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🎨</div>
            <h4 className="font-bold text-slate-900 text-sm">Customizable Design</h4>
            <p className="text-xs text-slate-600">Match your institution's branding perfectly</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile Responsive</h4>
            <p className="text-xs text-slate-600">Works perfectly on all devices</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔗</div>
            <h4 className="font-bold text-slate-900 text-sm">ERP + Website Integration</h4>
            <p className="text-xs text-slate-600">Real-time data display from your ERP</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🔒</div>
            <h4 className="font-bold text-slate-900 text-sm">Secure Portals</h4>
            <p className="text-xs text-slate-600">Parent and student portals with secure login</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📈</div>
            <h4 className="font-bold text-slate-900 text-sm">SEO Optimized</h4>
            <p className="text-xs text-slate-600">Built-in SEO for better search ranking</p>
          </div>
        </div>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Get Both with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Your institution's public website is its digital front door. Your ERP is its administrative backbone. They should work together, not separately.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon gives you both—a powerful public website and a comprehensive educational ERP. All in one platform. Consistent branding. Seamless experience. One subscription. One login. One system.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for integrated ERP + website.</strong> Your public face and your administrative backbone—unified.
        </p>
      </div>
    </>
  );
};

export default ERPWebsite;